import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import MagicButton from 'components/MagicButton/MagicButton';
import { useStartHaveListImportAsyncMutation, useStartWantListImportAsyncMutation } from 'store/services/import/importApi';
import {
  selectImportJob,
  selectImportProgress,
  selectIsImporting,
  importJobStarted,
  resetImportJob,
  setImportModalOpen,
} from 'store/slices/importSlice';
import './ImportCardsModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCardAdd: (identifier: number | string, cardData?: any, searchMode?: 'any' | 'specific') => void;
  useOracleId?: boolean;
  isHaveList?: boolean;
  /** When true, skip input UI and show only progress/results (opened from pill) */
  statusMode?: boolean;
}

type InputMode = 'paste' | 'csv';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export default function ImportCardsModal({ isOpen, onClose, onCardAdd, useOracleId = false, isHaveList = false, statusMode = false }: Props) {
  const dispatch = useDispatch();
  const [inputMode, setInputMode] = useState<InputMode>('paste');
  const [pasteText, setPasteText] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Redux import state
  const job = useSelector((state: RootState) => selectImportJob(state));
  const progress = useSelector((state: RootState) => selectImportProgress(state));
  const isImportingAsync = useSelector((state: RootState) => selectIsImporting(state));

  // Async mutations
  const [startHaveListImport] = useStartHaveListImportAsyncMutation();
  const [startWantListImport] = useStartWantListImportAsyncMutation();

  // Determine if we're showing async progress (job is active or completed)
  const hasActiveJob = job.status !== 'idle';
  const showProgressView = statusMode || isImportingAsync || hasActiveJob;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        if (file.size > MAX_FILE_SIZE) {
          toast.error('File size must be less than 20MB');
          return;
        }
        setCsvFile(file);
      } else {
        toast.error('Please upload a CSV file');
      }
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.size > MAX_FILE_SIZE) {
        toast.error('File size must be less than 20MB');
        return;
      }
      setCsvFile(file);
    }
  }, []);

  const handleImport = async () => {
    setIsSubmitting(true);

    try {
      let body: FormData | { text: string };

      if (inputMode === 'paste') {
        if (!pasteText.trim()) {
          toast.error('Please enter card names to import');
          setIsSubmitting(false);
          return;
        }
        body = { text: pasteText };
      } else {
        if (!csvFile) {
          toast.error('Please select a CSV file to import');
          setIsSubmitting(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', csvFile);
        body = formData;
      }

      // Use the async mutation for the appropriate list type
      const startImport = isHaveList ? startHaveListImport : startWantListImport;
      const response = await startImport(body).unwrap();

      // Response is { jobId, totalRows } from HTTP 202
      dispatch(importJobStarted({
        jobId: response.jobId,
        totalRows: response.totalRows,
        targetList: isHaveList ? 'have' : 'want',
      }));

      // Modal transitions to progress view via Redux state
    } catch (error: any) {
      console.error('Import error:', error);

      if (error?.status === 409 || error?.data?.error === 'IMPORT_ALREADY_ACTIVE') {
        toast.warning(error?.data?.message || 'You already have an import in progress.');
      } else if (error?.data?.message) {
        toast.error(error.data.message);
      } else if (error?.data?.errors && Array.isArray(error.data.errors)) {
        toast.error(`Import failed: ${error.data.errors[0]?.error || 'Unknown error'}`);
      } else {
        toast.error('An error occurred during import. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setPasteText('');
    setCsvFile(null);
    dispatch(resetImportJob());
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (isImportingAsync) {
      // Import is running — just close the modal, pill will show progress
      dispatch(setImportModalOpen(false));
      onClose();
    } else {
      // No active import — full reset
      if (!hasActiveJob) {
        handleReset();
      }
      dispatch(setImportModalOpen(false));
      onClose();
    }
  };

  if (!isOpen) return null;

  // Render progress/results view when an async job is active or done
  const renderProgressView = () => {
    const isActive = job.status === 'pending' || job.status === 'processing';
    const isComplete = job.status === 'completed';
    const isFailed = job.status === 'failed';

    return (
      <>
        {/* Progress bar and live stats during import */}
        {isActive && (
          <div className="import-progress-section">
            <div className="import-progress-bar">
              <div
                className={`import-progress-fill ${progress.percentage === 0 ? 'import-progress-indeterminate' : ''}`}
                style={progress.percentage > 0 ? { width: `${progress.percentage}%` } : undefined}
              />
            </div>
            <p className="import-progress-text">
              {progress.percentage > 0
                ? `Processing row ${progress.processedRows.toLocaleString()} of ${progress.totalRows.toLocaleString()}`
                : 'Starting import...'}
            </p>
            <div className="import-progress-stats">
              <span className="import-stat-matched">{job.matched.toLocaleString()} matched</span>
              <span className="import-stat-skipped">{job.skipped.toLocaleString()} skipped</span>
              <span className="import-stat-failed">{job.failed.toLocaleString()} failed</span>
            </div>
            <p className="import-progress-hint">
              You can close this dialog — the import will continue in the background.
            </p>
          </div>
        )}

        {/* Completion summary */}
        {isComplete && (
          <div className="import-result-section">
            <div className="import-result-summary">
              <div className="import-result-stat success">
                <FontAwesomeIcon icon="check-circle" />
                <span>{job.matched.toLocaleString()} Imported</span>
              </div>
              {job.skipped > 0 && (
                <div className="import-result-stat skipped">
                  <FontAwesomeIcon icon="forward" />
                  <span>{job.skipped.toLocaleString()} Skipped</span>
                </div>
              )}
              {job.failed > 0 && (
                <div className="import-result-stat failed">
                  <FontAwesomeIcon icon="times-circle" />
                  <span>{job.failed.toLocaleString()} Failed</span>
                </div>
              )}
            </div>

            {/* Skip reasons breakdown */}
            {Object.keys(job.skipReasons).length > 0 && (
              <details className="import-failed-details">
                <summary>Skip reasons</summary>
                <div className="import-failed-list">
                  {Object.entries(job.skipReasons).map(([reason, count]) => (
                    <div key={reason} className="import-failed-item">
                      <span className="import-failed-name">{reason}</span>
                      <span className="import-failed-error">{count}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Failed cards detail */}
            {job.failedCards.length > 0 && (
              <details className="import-failed-details">
                <summary>View failed cards ({job.failedCards.length})</summary>
                <div className="import-failed-list">
                  {job.failedCards.slice(0, 50).map((card, index) => (
                    <div key={index} className="import-failed-item">
                      <span className="import-failed-name">{card.cardName}</span>
                      <span className="import-failed-error">{card.error}</span>
                    </div>
                  ))}
                  {job.failedCards.length > 50 && (
                    <div className="import-failed-item">
                      <span className="import-failed-name" style={{ color: '#999' }}>
                        ...and {job.failedCards.length - 50} more
                      </span>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Failure state */}
        {isFailed && (
          <div className="import-result-section">
            <div className="import-result-pending" style={{ background: 'rgba(248, 113, 113, 0.1)', color: '#f87171' }}>
              <FontAwesomeIcon icon="times-circle" style={{ fontSize: 24 }} />
              <span>Import Failed</span>
              {job.error && (
                <p className="import-pending-hint">{job.error}</p>
              )}
            </div>

            {/* Show partial results if any cards were processed */}
            {job.matched > 0 && (
              <div className="import-result-summary" style={{ marginTop: 16 }}>
                <div className="import-result-stat success">
                  <FontAwesomeIcon icon="check-circle" />
                  <span>{job.matched.toLocaleString()} Imported before failure</span>
                </div>
              </div>
            )}

            {job.failedCards.length > 0 && (
              <details className="import-failed-details">
                <summary>View failed cards ({job.failedCards.length})</summary>
                <div className="import-failed-list">
                  {job.failedCards.slice(0, 50).map((card, index) => (
                    <div key={index} className="import-failed-item">
                      <span className="import-failed-name">{card.cardName}</span>
                      <span className="import-failed-error">{card.error}</span>
                    </div>
                  ))}
                  {job.failedCards.length > 50 && (
                    <div className="import-failed-item">
                      <span className="import-failed-name" style={{ color: '#999' }}>
                        ...and {job.failedCards.length - 50} more
                      </span>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}
      </>
    );
  };

  // Render input UI (paste/CSV selection)
  const renderInputView = () => (
    <>
      <div className="import-mode-selector">
        <label>Input Method:</label>
        <select
          value={inputMode}
          onChange={(e) => {
            setInputMode(e.target.value as InputMode);
            setPasteText('');
            setCsvFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}
          disabled={isSubmitting}
        >
          <option value="paste">Copy/Paste</option>
          <option value="csv">Upload CSV</option>
        </select>
      </div>

      {inputMode === 'paste' ? (
        <div className="import-paste-section">
          <textarea
            className="import-paste-textarea"
            placeholder={isHaveList ? `Enter card names with sets, one per line:

2 Lightning Bolt [LEA]
Black Lotus [LEA]
4x Counterspell [LEA] NM

Set code is REQUIRED for Have List:
• Use [SET] after card name
• Example: Lightning Bolt [LEA]
• Defaults: Qty=1, Lang=EN, Cond=NM, Foil=false` : `Enter card names, one per line:

Lightning Bolt
4 Counterspell
2x Brainstorm

Supported formats:
• Card Name (defaults to 1)
• 4 Card Name
• 4x Card Name`}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
      ) : (
        <div
          className={`import-dropzone ${isDragging ? 'dragging' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={isSubmitting}
          />

          {csvFile ? (
            <div className="import-file-info">
              <FontAwesomeIcon icon="file-csv" size="2x" />
              <p>{csvFile.name}</p>
              <p className="import-file-size">
                {(csvFile.size / 1024).toFixed(1)} KB
              </p>
              <button
                className="import-file-remove"
                onClick={() => {
                  setCsvFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                disabled={isSubmitting}
              >
                Remove
              </button>
            </div>
          ) : (
            <>
              <FontAwesomeIcon icon="cloud-upload-alt" size="3x" />
              <p>Drop CSV file here or</p>
              <button
                className="import-browse-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
              >
                Choose File
              </button>
              <p className="import-format-hint">
                Max file size: 20MB<br />
                Format: Card Name, Quantity, Set{isHaveList ? ' (required)' : ''}, Language, Condition, Foil
              </p>
            </>
          )}
        </div>
      )}
    </>
  );

  const isActive = job.status === 'pending' || job.status === 'processing';
  const isDone = job.status === 'completed' || job.status === 'failed';

  return createPortal(
    <div className="import-modal-overlay" onClick={handleClose}>
      <div className="import-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="import-modal-header">
          <h2>
            {showProgressView
              ? (isActive ? 'Importing Cards...' : isDone ? 'Import Results' : 'Import Cards')
              : `Import Cards to ${isHaveList ? 'Have List' : 'Want List'}`
            }
          </h2>
          <button className="import-modal-close" onClick={handleClose}>
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        <div className="import-modal-body">
          {showProgressView ? renderProgressView() : renderInputView()}
        </div>

        <div className="import-modal-footer">
          {isActive ? (
            <MagicButton
              label="Minimize"
              color="grey"
              onPress={handleClose}
            />
          ) : isDone ? (
            <>
              <MagicButton
                label="Done"
                color="grey"
                onPress={() => {
                  handleReset();
                  handleClose();
                }}
              />
              <MagicButton
                label="Import More"
                color="purple"
                onPress={handleReset}
              />
            </>
          ) : (
            <>
              <MagicButton
                label="Cancel"
                color="grey"
                onPress={handleClose}
                isDisabled={isSubmitting}
              />
              <MagicButton
                label={isSubmitting ? 'Starting...' : 'Import'}
                color="purple"
                onPress={handleImport}
                isDisabled={isSubmitting || (inputMode === 'paste' ? !pasteText.trim() : !csvFile)}
                isLoading={isSubmitting}
              />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
